import React, { useState } from 'react';
import { DEFAULT_PROMPTS, CATEGORY_DESCRIPTION_SECTIONS } from '../../constants/sectionConfig';
import { useTranslation } from 'react-i18next';
import { BrandSelector } from '../shared/BrandSelector';
import { useData } from '../../contexts/DataContext';

interface Props {
  sectionIntensities: Record<string, number>;
  onChange: (intensities: Record<string, number>) => void;
  changeBrand: (brand: string) => void;
  changeStaffFavorites: (staffFavorites: string) => void;
  changeTopPicks: (topPicks: string) => void;
}

const SectionSelection: React.FC<Props> = ({
  sectionIntensities,
  onChange,
  changeBrand,
  changeStaffFavorites,
  changeTopPicks,
}) => {
  const { brands } = useData();
  const { t } = useTranslation();
  const [selectedBrand, setSelectedBrand] = useState('');
  const [staffFavorites, setStaffFavorites] = useState('');
  const [topPicks, setTopPicks] = useState('');
  const handleSliderChange = (sectionId: string, value: number) => {
    onChange({
      ...sectionIntensities,
      [sectionId]: value,
    });
  };

  const handleBrandChange = (brand: string) => {
    setSelectedBrand(brand);
    changeBrand(brand);
  };

  const handleStaffFavoritesChange = (staffFavorites: string) => {
    setStaffFavorites(staffFavorites);
    changeStaffFavorites(staffFavorites);
  };

  const handleTopPicksChange = (topPicks: string) => {
    setTopPicks(topPicks);
    changeTopPicks(topPicks);
  };

  return (
    <div className="mt-8 mb-6">
      <h2 className="text-xl font-semibold mb-4">{t('contentSections')}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(CATEGORY_DESCRIPTION_SECTIONS)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .filter(([sectionId, config]) => config.display)
          .map(([sectionId, config]) => {
            let intensity =
              sectionIntensities[sectionId as keyof typeof sectionIntensities] || (0 as number);
            if (config.hideSlider) {
              intensity = config.defaultIntensity;
            }
            return (
              <div
                key={sectionId}
                className={`flex flex-col space-y-2 p-4 rounded-lg shadow transition-colors duration-200 
                            ${getIntensityColor(intensity)}`}
              >
                <div className="flex items-center justify-between">
                  <label className="font-medium text-gray-700 dark:text-gray-200">
                    {t(config.label)}
                  </label>
                  <span className="text-sm text-gray-500">{intensity}</span>
                </div>
                {sectionId === 'brand' && (
                  <BrandSelector
                    brands={brands || []}
                    selectedBrand={selectedBrand}
                    onSelect={handleBrandChange}
                  />
                )}

                {config.customInput && (
                  <textarea
                    rows={3}
                    value={sectionId === 'staff_favorites' ? staffFavorites : topPicks}
                    onChange={(e) =>
                      sectionId === 'staff_favorites'
                        ? handleStaffFavoritesChange(e.target.value)
                        : handleTopPicksChange(e.target.value)
                    }
                    className="w-full p-2 border border-gray-300 dark:border-gray-700 rounded-md dark:bg-gray-800 dark:text-gray-200"
                  />
                )}

                {!config.hideSlider && (
                  <>
                    <input
                      type="range"
                      min="0"
                      max="5"
                      value={intensity}
                      onChange={(e) => handleSliderChange(sectionId, parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                    />

                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{t('none')}</span>
                      <span>{t('full')}</span>
                    </div>
                  </>
                )}

                <div className="flex items-center justify-between">
                  <label className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {t(
                      DEFAULT_PROMPTS?.levels[intensity as keyof typeof DEFAULT_PROMPTS.levels]
                        ?.description
                    ) || ''}
                  </label>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

const getIntensityColor = (level: number) => {
  const colors = {
    0: 'bg-white dark:bg-white',
    1: 'bg-green-50 dark:bg-green-900',
    2: 'bg-green-100 dark:bg-green-800',
    3: 'bg-green-200 dark:bg-green-700',
    4: 'bg-green-300 dark:bg-green-600',
    5: 'bg-green-400 dark:bg-green-500',
  };
  return colors[level as keyof typeof colors] || colors[0];
};

export default SectionSelection;
